import React, { useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Divider,
  useTheme,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Chip,
} from "@mui/material";
import { Person as PersonIcon } from "@mui/icons-material";
import IconButton from "@mui/material/IconButton";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

const OTHER_SCORE_ITEMS = [
  { label: "Communication Score", key: "communication_skill_score" },
  { label: "Confidence Quotient Score", key: "confidance_personality_score" },
  { label: "Thinking Clarity Score", key: "thought_process_score" },
  { label: "Professionalism Score", key: "professionalism_score" },
  { label: "Role Fit Score", key: "tech_rolefit_score" },
];

const formatOtherScore = (value) => {
  if (value == null || value === "") return "0.00";
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return n.toFixed(1);
};

const pickOtherScore = (details, key) => {
  if (!details) return null;
  const camelKey = key.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
  return details[key] ?? details[camelKey] ?? null;
};

const CandidateInfoCard = ({
  candidate,
  details,
  onOpenLogs,
  onOpenOtherScores,
  onOpenFeedback,
}) => {
  const theme = useTheme();
  const [otherScoresOpen, setOtherScoresOpen] = useState(false);

  return (
    <Card
      elevation={2}
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        boxShadow:
          theme.palette.mode === "dark"
            ? "0px 1px 3px rgba(0, 0, 0, 0.3)"
            : "0px 1px 3px rgba(0, 0, 0, 0.1)",
        borderRadius: 3,
      }}
    >
      <CardContent sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 2 }}>
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            Candidate & Interview Information
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={() => onOpenFeedback?.()}
            sx={{ textTransform: "none", fontWeight: 650, flexShrink: 0 }}
          >
            View Candidate Feedback
          </Button>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center",  minWidth: 0 }}>
          <Avatar
            sx={{
              width: 42,
              height: 42,
              bgcolor: theme.palette.primary.main,
              mr: 1,
            }}
          >
            <PersonIcon sx={{ fontSize: 30 }} />
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: theme.palette.text.primary,
                overflowWrap: "anywhere",
                textTransform: "capitalize",
              }}
            >
              {candidate.name}
            </Typography>
            <Typography
                variant="caption"
                sx={{ fontWeight: 500, color: theme.palette.text.secondary, overflowWrap: "anywhere" }}
              >
                {candidate.position}
              </Typography>
            {/* <Typography
              variant="caption"
              color="text.secondary"             
              sx={{ overflowWrap: "anywhere"}}
            >
              {candidate.email}
            </Typography> */}
          </Box>
        </Box>

        <Divider sx={{ my: 1.5 }} />

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr 1fr",
              md: "repeat(7, minmax(0, 1fr))",
            },
            gap: 1.5,
          }}
        >
          {/* <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
            <WorkIcon
              sx={{ fontSize: 20, color: theme.palette.text.secondary, mr: 1.5 }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                Position
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontWeight: 500, color: theme.palette.text.primary, overflowWrap: "anywhere" }}
              >
                {candidate.position}
              </Typography>
            </Box>
          </Box> */}

          {details && (
            <>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  sx={{ mb: 0.5 }}
                >
                  Interview Date
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 500, color: theme.palette.text.primary, overflowWrap: "anywhere" }}
                >
                  {details.date}
                </Typography>
              </Box>

              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  sx={{ mb: 0.5 }}
                >
                  Total Duration
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 500, color: theme.palette.text.primary, overflowWrap: "anywhere" }}
                >
                  {details.duration}
                </Typography>
              </Box>

              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  sx={{ mb: 0.5 }}
                >
                  Total Behaviour Score
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    color: theme.palette.text.primary,
                    overflowWrap: "anywhere",
                  }}
                >
                  {details.totalBehaviourScore != null
                    ? details.totalBehaviourScore: "0"}/5
                </Typography>
              </Box>

              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  sx={{ mb: 0.5 }}
                >
                  Total AI Response Score
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    color: theme.palette.text.primary,
                    overflowWrap: "anywhere",
                  }}
                >
                  {details.totalAiScore != null ? details.totalAiScore: "0"}/5
                </Typography>
              </Box>

              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  
                >
                  Other Scores
                </Typography>
                <Typography
                  variant="body2"
                  py={0.5}
                  sx={{ fontWeight: 500, color: theme.palette.text.primary, overflowWrap: "anywhere" }}
                >                
                   <Chip label="View"
                     variant="outlined"
                     color="primary"
                     size="small"
                   
                     sx={{ mr: 0.4, cursor: "pointer", height: 21,width: 60 }}
                     onClick={() => {
                       onOpenOtherScores?.();
                       setOtherScoresOpen(true);
                     }}
                   />
                </Typography>
                {/* <Button
                  size="small"
                  variant="text"
                  //startIcon={<InfoOutlinedIcon sx={{ fontSize: 18 }} />}
                  onClick={() => setOtherScoresOpen(true)}
                  aria-label="View other scores"
                  sx={{ px: 0, textTransform: "none", fontWeight: 600, minWidth: "auto", textAlign:'center' }}
                >
                  View Scores
                </Button> */}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  sx={{ mb: 0.5 }}
                >
                  Questions Answered
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 500, color: theme.palette.text.primary, overflowWrap: "anywhere" }}
                >
                  {details.questionsAnswered}
                </Typography>
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  sx={{ mb: 0.5 }}
                >
                  View Logs
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 500, color: theme.palette.text.primary, overflowWrap: "anywhere" }}
                >                
                   <VisibilityIcon
                     color="primary"
                     sx={{ mr: 0.4, cursor: "pointer" }}
                     onClick={() => onOpenLogs?.(details.interviewId,candidate.name)}
                   />
                </Typography>
              </Box>
            </>
          )}
        </Box>
      </CardContent>

      <Dialog
        open={otherScoresOpen}
        onClose={() => setOtherScoresOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ px: 2.5, py: 1.75, display: "flex", alignItems: "center", justifyContent: "space-between" }}>Other Scores
        <Button variant="outlined" color="secondary" size="small" onClick={() => setOtherScoresOpen(false)}>Close
          <CloseRoundedIcon fontSize="small" />
        </Button>
        
        </DialogTitle>
        
        <DialogContent dividers>
          <Stack spacing={1.5} divider={<Divider flexItem />}>
            {OTHER_SCORE_ITEMS.map(({ label, key }) => (
              <Box
                key={key}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                  py: 0.25,
                }}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ flex: 1, minWidth: 0}}
                >
                  {label}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, flexShrink: 0 }}>
                  {formatOtherScore(pickOtherScore(details, key))}
                </Typography>
              </Box>
            ))}
          </Stack>
        </DialogContent>
        
      </Dialog>
    </Card>
  );
};

export default CandidateInfoCard;
